import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  // Read from sessionStorage first (session-only login), then localStorage (remember me).
  const [user, setUser] = useState(() => {
    const raw = sessionStorage.getItem("sdm_user") || localStorage.getItem("sdm_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem("sdm_token") || localStorage.getItem("sdm_token");
    if (token && !user) {
      api.get("/auth/me").then((r) => {
        setUser(r.data);
        const store = sessionStorage.getItem("sdm_token") ? sessionStorage : localStorage;
        store.setItem("sdm_user", JSON.stringify(r.data));
      }).catch(() => {});
    }
  }, [user]);

  // Heartbeat — pings the backend every 60s while the tab is active so the
  // escalation router knows the agent is actually connected. Sprint 34P.13.
  useEffect(() => {
    if (!user) return undefined;
    const ping = () => {
      if (document.visibilityState === "visible") {
        api.post("/users/me/heartbeat").catch(() => {});
      }
    };
    ping(); // immediate hit on login/mount
    const iv = setInterval(ping, 60000);
    const onVis = () => { if (document.visibilityState === "visible") ping(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [user]);

  const login = async (email, password, remember = true) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Persistent storage when "remember me" is checked; session-only otherwise.
      const store = remember ? localStorage : sessionStorage;
      const other = remember ? sessionStorage : localStorage;
      store.setItem("sdm_token", data.access_token);
      if (data.refresh_token) store.setItem("sdm_refresh", data.refresh_token);
      store.setItem("sdm_user", JSON.stringify(data.user));
      other.removeItem("sdm_token");
      other.removeItem("sdm_refresh");
      other.removeItem("sdm_user");
      sessionStorage.setItem("sdm_training_check", "1");
      setUser(data.user);
      // Mark the agent as online right after login.
      api.patch("/users/me/presence", { presence: "available" }).catch(() => {});
      return data.user;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try { await api.patch("/users/me/presence", { presence: "offline", reason: "logout" }); } catch (_) { /* ignore */ }
    localStorage.removeItem("sdm_token");
    localStorage.removeItem("sdm_refresh");
    localStorage.removeItem("sdm_user");
    sessionStorage.removeItem("sdm_token");
    sessionStorage.removeItem("sdm_refresh");
    sessionStorage.removeItem("sdm_user");
    setUser(null);
    window.location.href = "/login";
  };

  return <Ctx.Provider value={{ user, login, logout, loading, setUser }}>{children}</Ctx.Provider>;
}
export const useAuth = () => useContext(Ctx);
